import { useCallback, useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useLocation } from "react-router-dom";
import { CommercialOnboardingPlanStep } from "@/components/commercial-plan-selector";
import { getLearnMoreHrefWithPrice } from "@/components/modal/proactive-upsell/learn-more-href";
import { startProactiveCheckout } from "@/components/modal/proactive-upsell/proactive-checkout";
import {
  WizardContentWrapper,
  type StepRenderProps,
} from "@/components/onboarding/components/welcome-wizard";
import { APP_CONFIG } from "@/domains/global/config";
import { buildProactivePaywallEventPayload } from "@/domains/metrics/monetization";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { logger } from "@/modules/logger";
import {
  getDefaultMemAgentHoldToTalkKey,
  type MemAgentHoldToTalkKey,
} from "@/modules/mem-agent/hold-to-talk-key";
import { objectModule } from "@/modules/object";
import {
  CommercialPlanKey,
  getCompatibleCommercialCatalog,
  isCommercialRolloutActive,
  type CommercialPlanDefinition,
} from "@/modules/commercial-plans";
import { subscriptionModule } from "@/modules/subscriptions";
import {
  StripeCheckoutOnboardingStep,
  StripeCheckoutSource,
} from "@/modules/subscriptions/query-params";
import { toastModule } from "@/modules/toast";
import { clientEnvModule } from "@/modules/client-env";
import { windowModule } from "@/modules/window";
import { useAppStore } from "@/store";
import { PROACTIVE_PRICING_CLIENT_FALLBACK_SOURCE } from "@/store/subscriptions";
import { BillingStrategyEnum } from "@/store/pages/SettingsSubscriptionManagementPageStore/types";
import {
  ExistingUserMemAgentTransitionStepView,
  ExistingUserProactiveTrialStepView,
  ExistingUserPushToRememberStepView,
} from "./existing-user-onboarding-pages";
import { usePrepareMemAgentFirstMessageOnboarding } from "./mem-agent-first-message-step";

export const ExistingUserMemAgentIntroStep = ({ onComplete }: StepRenderProps) => (
  <ExistingUserMemAgentIntroStepView onComplete={onComplete} />
);

export const ExistingUserProactiveTrialStep = observer(({ onComplete }: StepRenderProps) => {
  const { store } = useAppStore();
  const location = useLocation();
  const [isSubscribing, setIsSubscribing] = useState(false);
  const didTrackPaywallView = useRef(false);
  const didTrackProactivePricingExposure = useRef(false);

  const isTrialEligible = store.subscriptions.isProactiveUpsellTrialEligible;
  const proactivePricingAssignment = store.subscriptions.proactivePricingAssignment;
  const proactivePricingError = store.subscriptions.proactivePricingAssignmentError;
  const proactivePricingIsLoading =
    store.subscriptions.proactivePricingAssignmentIsLoading ||
    (!proactivePricingAssignment && proactivePricingError === undefined);
  const proactivePrice = proactivePricingAssignment?.display_price;
  const learnMoreHref = getLearnMoreHrefWithPrice({
    learnMoreHref: APP_CONFIG.URLS.MEM_AGENT_DOCS,
    proactivePriceCents: proactivePricingAssignment?.display_price_cents,
  });
  const checkoutReturnConfirmationIsPending =
    store.onboarding.proactiveCheckoutReturnConfirmationIsPending;
  const checkoutResetKey = store.subscriptions.proactiveCheckoutResetKey;
  const commercialPlanSnapshot = store.platformInfo.commercialPlanSnapshot;
  const commercialCatalog = getCompatibleCommercialCatalog({
    catalog: store.platformInfo.commercialCatalog,
    snapshot: commercialPlanSnapshot,
  });
  const commercialRolloutIsActive = isCommercialRolloutActive({
    catalog: store.platformInfo.commercialCatalog,
    snapshot: commercialPlanSnapshot,
  });

  useEffect(() => {
    if (commercialRolloutIsActive) {
      return;
    }

    void store.subscriptions.preloadProactivePricingAssignment().catch(() => undefined);
  }, [commercialRolloutIsActive, store]);

  useEffect(() => {
    setIsSubscribing(false);
  }, [checkoutResetKey]);

  useEffect(() => {
    if (commercialRolloutIsActive || !proactivePricingAssignment) {
      return;
    }

    if (
      !didTrackProactivePricingExposure.current &&
      proactivePricingAssignment.source !== PROACTIVE_PRICING_CLIENT_FALLBACK_SOURCE
    ) {
      didTrackProactivePricingExposure.current = true;
      trackEvent(TrackedEvent.ExperimentStarted, {
        experiment: proactivePricingAssignment.experiment_key,
        variant: proactivePricingAssignment.variant_key,
      });
    }

    if (!didTrackPaywallView.current) {
      didTrackPaywallView.current = true;
      trackEvent(
        TrackedEvent.PaywallView,
        buildProactivePaywallEventPayload({ store, source: "onboarding" })
      );
    }
  }, [commercialRolloutIsActive, proactivePricingAssignment, store]);

  const retryPricingAssignment = useCallback(() => {
    void store.subscriptions.fetchProactivePricingAssignment({ trackExposure: false });
  }, [store.subscriptions]);

  const handleLearnMore = useCallback(() => {
    void windowModule.openInNewTab({ url: new URL(learnMoreHref) });
  }, [learnMoreHref]);

  const handleSkip = useCallback(() => {
    trackEvent(
      TrackedEvent.PaywallCancelClick,
      buildProactivePaywallEventPayload({ store, source: "onboarding" })
    );
    onComplete();
  }, [onComplete, store]);

  const handleSubscribe = useCallback(async () => {
    if (isSubscribing || checkoutReturnConfirmationIsPending) return;

    if (store.subscriptions.shouldUseAppleProactiveUpgradeModal) {
      store.subscriptions.openProactiveUpsell();
      return;
    }

    trackEvent(
      TrackedEvent.PaywallUpgradeClick,
      buildProactivePaywallEventPayload({ store, source: "onboarding" })
    );
    setIsSubscribing(true);

    try {
      const checkoutResult = await startProactiveCheckout({
        checkoutOnboardingStep: StripeCheckoutOnboardingStep.ExistingUserProactiveTrial,
        checkoutSource: StripeCheckoutSource.Onboarding,
        location,
        store,
      });

      if (checkoutResult.didStartExternalNavigation) {
        return;
      }

      store.onboarding.startExistingUserProactiveTrialWelcome();
      store.platformInfo.openWelcomeToProactiveModal();
    } catch (error) {
      trackEvent(TrackedEvent.PaywallUpgradeFailed, {
        ...buildProactivePaywallEventPayload({ store, source: "onboarding" }),
        failure_stage: "checkout_start",
        failure_reason: "checkout_start_error",
        error_message: error instanceof Error ? error.message : "Unknown checkout error",
      });
      logger.error({
        message: "[ExistingUserProactiveTrialStep] Failed to start proactive checkout",
        info: { error: objectModule.safeErrorAsJson(error as Error) },
      });
      toastModule.triggerToast({
        content:
          error instanceof Error
            ? error.message
            : "Failed to start Proactive checkout. Please try again.",
      });
      setIsSubscribing(false);
    }
  }, [checkoutReturnConfirmationIsPending, isSubscribing, location, store]);

  const handleChooseCommercialPlan = useCallback(
    async (plan: CommercialPlanDefinition) => {
      if (
        isSubscribing ||
        checkoutReturnConfirmationIsPending ||
        plan.key === CommercialPlanKey.Mem ||
        !plan.checkoutAvailable
      ) {
        return;
      }

      trackEvent(
        TrackedEvent.PaywallUpgradeClick,
        buildProactivePaywallEventPayload({ store, source: "onboarding" })
      );
      setIsSubscribing(true);

      try {
        const checkoutResult = await subscriptionModule.createCheckoutSessionAndNavigate({
          billingStrategy: BillingStrategyEnum.MONTHLY,
          checkoutOnboardingStep: StripeCheckoutOnboardingStep.ExistingUserProactiveTrial,
          checkoutSource: StripeCheckoutSource.Onboarding,
          commercialPlanKey: plan.key,
          extraUsageBlocks: 0,
          location,
          store,
        });

        if (!checkoutResult.didStartExternalNavigation) {
          store.onboarding.startExistingUserProactiveTrialWelcome();
          store.platformInfo.openWelcomeToProactiveModal();
        }
      } catch (error) {
        logger.error({
          message: "[ExistingUserProactiveTrialStep] Failed to start commercial checkout",
          info: { error: objectModule.safeErrorAsJson(error as Error) },
        });
        toastModule.triggerToast({
          content: "Failed to start checkout. Please try again.",
        });
        setIsSubscribing(false);
      }
    },
    [checkoutReturnConfirmationIsPending, isSubscribing, location, store]
  );

  if (commercialRolloutIsActive && commercialCatalog && commercialPlanSnapshot) {
    return (
      <WizardContentWrapper>
        <CommercialOnboardingPlanStep
          actionIsLoading={isSubscribing}
          catalog={commercialCatalog}
          onChoosePlan={plan => void handleChooseCommercialPlan(plan)}
          onContinueFree={handleSkip}
          snapshot={commercialPlanSnapshot}
        />
      </WizardContentWrapper>
    );
  }

  return (
    <ExistingUserProactiveTrialStepView
      canSkip
      isActionLoading={isSubscribing || checkoutReturnConfirmationIsPending}
      isTrialEligible={isTrialEligible}
      isWindows={clientEnvModule.isWindows()}
      proactivePrice={proactivePrice}
      pricingError={proactivePricingError}
      pricingIsLoading={proactivePricingIsLoading}
      onLearnMore={handleLearnMore}
      onRetryPricing={retryPricingAssignment}
      onSkip={handleSkip}
      onSubscribe={handleSubscribe}
    />
  );
});

export const ExistingUserMemAgentFirstMessageStep = observer((_props: StepRenderProps) => {
  usePrepareMemAgentFirstMessageOnboarding();

  const { store } = useAppStore();
  const holdToTalkKey =
    store.spaceAccountSettings.memAgent.holdToTalkKey ?? getDefaultMemAgentHoldToTalkKey();
  const [displayHoldToTalkKey, setDisplayHoldToTalkKey] =
    useState<MemAgentHoldToTalkKey>(holdToTalkKey);
  const [isSavingHoldToTalkKey, setIsSavingHoldToTalkKey] = useState(false);

  useEffect(() => {
    setDisplayHoldToTalkKey(holdToTalkKey);
  }, [holdToTalkKey]);

  const handleShortcutChange = useCallback(
    (nextKey: MemAgentHoldToTalkKey) => {
      if (isSavingHoldToTalkKey || nextKey === displayHoldToTalkKey) return;

      const previousHoldToTalkKey = displayHoldToTalkKey;
      setDisplayHoldToTalkKey(nextKey);
      setIsSavingHoldToTalkKey(true);
      void store.spaceAccountSettings
        .updateSettingsUsingSyncOperation({
          mem_agent: {
            hold_to_talk_key: nextKey,
          },
        })
        .then(
          () => setIsSavingHoldToTalkKey(false),
          () => {
            setDisplayHoldToTalkKey(previousHoldToTalkKey);
            setIsSavingHoldToTalkKey(false);
            toastModule.triggerToast({
              content: "Couldn't save hold-to-talk key. Please try again.",
            });
          }
        );
    },
    [displayHoldToTalkKey, isSavingHoldToTalkKey, store.spaceAccountSettings]
  );

  return (
    <ExistingUserMemAgentFirstMessageStepView
      holdToTalkKey={displayHoldToTalkKey}
      isSavingHoldToTalkKey={isSavingHoldToTalkKey}
      isWindows={clientEnvModule.isWindows()}
      onHoldToTalkKeyChange={handleShortcutChange}
    />
  );
});

export interface ExistingUserMemAgentIntroStepViewProps {
  onComplete: () => void;
}

/** Introduces Mem Agent to legacy users who completed onboarding before Agent onboarding existed. */
export const ExistingUserMemAgentIntroStepView = ({
  onComplete,
}: ExistingUserMemAgentIntroStepViewProps) => (
  <ExistingUserMemAgentTransitionStepView onComplete={onComplete} />
);

export interface ExistingUserMemAgentFirstMessageStepViewProps {
  holdToTalkKey: MemAgentHoldToTalkKey;
  isSavingHoldToTalkKey?: boolean;
  isWindows?: boolean;
  onHoldToTalkKeyChange: (nextKey: MemAgentHoldToTalkKey) => void;
}

/** Teaches existing desktop users the Push-to-Remember hold-to-talk shortcut. */
export const ExistingUserMemAgentFirstMessageStepView = ({
  holdToTalkKey,
  isSavingHoldToTalkKey = false,
  isWindows,
  onHoldToTalkKeyChange,
}: ExistingUserMemAgentFirstMessageStepViewProps) => (
  <ExistingUserPushToRememberStepView
    holdToTalkKey={holdToTalkKey}
    isSavingHoldToTalkKey={isSavingHoldToTalkKey}
    isWindows={isWindows}
    onHoldToTalkKeyChange={onHoldToTalkKeyChange}
  />
);
